import type {CustomDealCategory} from '../../../../../types';

import {AppstoreOutlined, SortAscendingOutlined} from '@ant-design/icons';
import {BottomSheet, Button, Checkbox, Radio} from '@ds/components';
import {useState} from 'react';

import FilterChip from '../../../../../components/common/FilterChip/FilterChip';
import {
  CUSTOM_DEAL_BADGE_OPTIONS,
  CUSTOM_DEAL_SORT_OPTIONS,
  CUSTOM_DEAL_STOCK_STATUS_OPTIONS
} from '../../../../../constants';

type DealDetailFiltersState = {
  search: string | null;
  stock_status: string | number | null;
  sort: string | null;
  badge: string | number | null;
  categories: number[];
};

type DealDetailFiltersProps = {
  filters: DealDetailFiltersState;
  onChangeFilter: (name: string, value: string | number | null | number[]) => void;
  dealCategories?: CustomDealCategory[];
};

const DealDetailFilters: React.FC<DealDetailFiltersProps> = ({
  filters,
  onChangeFilter,
  dealCategories = []
}) => {
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [selectedValue, setSelectedValue] = useState<string | number | null>(null);
  const [selectedCategories, setSelectedCategories] = useState(filters.categories || []);

  const openModal = (name: string) => {
    setActiveFilter(name);
    if (name === 'categories') {
      setSelectedCategories(filters.categories || []);
      return;
    }
    const nextValue = filters[name as Exclude<keyof DealDetailFiltersState, 'categories'>];
    setSelectedValue(nextValue ?? null);
  };

  const closeModal = () => {
    setActiveFilter(null);
    setSelectedValue(null);
    setSelectedCategories(filters.categories || []);
  };

  const currentOptions =
    activeFilter === 'badge'
      ? CUSTOM_DEAL_BADGE_OPTIONS
      : activeFilter === 'sort'
        ? CUSTOM_DEAL_SORT_OPTIONS
        : CUSTOM_DEAL_STOCK_STATUS_OPTIONS;

  const currentTitle =
    activeFilter === 'badge'
      ? 'فروش'
      : activeFilter === 'sort'
        ? 'مرتب‌سازی'
        : activeFilter === 'stock_status'
          ? 'موجودی'
          : 'دسته‌بندی';

  const renderButton = (name: string, label: string, icon?: React.ReactNode) => {
    const hasValue =
      name === 'categories'
        ? Boolean(filters.categories?.length)
        : filters[name as keyof DealDetailFiltersState] != null;

    return (
      <FilterChip
        key={name}
        label={label}
        icon={icon}
        active={hasValue}
        onClick={() => openModal(name)}
        onClear={
          hasValue ? () => onChangeFilter(name, name === 'categories' ? [] : null) : undefined
        }
      />
    );
  };

  return (
    <>
      <div className="flex overflow-x-auto border-b border-gray-02 px-4 py-2">
        {renderButton(
          'sort',
          'مرتب‌سازی',
          <SortAscendingOutlined className="me-1 text-body-lg" aria-hidden />
        )}
        {renderButton('badge', 'فروش')}
        {renderButton('stock_status', 'موجودی')}
        {renderButton(
          'categories',
          'دسته‌بندی',
          <AppstoreOutlined className="me-1 text-body-lg" aria-hidden />
        )}
      </div>
      <BottomSheet
        show={Boolean(activeFilter)}
        onClose={closeModal}
        header={
          <div className="flex flex-col items-center px-4 py-2 text-subtitle-md text-neutral-fg">
            <span className="mb-4 h-1 w-8 rounded-full bg-gray-03" aria-hidden />
            <span>{currentTitle}</span>
          </div>
        }
      >
        {activeFilter === 'categories' ? (
          <div className="flex flex-col px-4 py-2">
            {dealCategories.map((category) => (
              <div key={category.id} className="py-3">
                <Checkbox
                  checked={selectedCategories.includes(category.id)}
                  onChange={() => {
                    setSelectedCategories((current) =>
                      current.includes(category.id)
                        ? current.filter((id) => id !== category.id)
                        : [...current, category.id]
                    );
                  }}
                >
                  {category.title} {`(${category.product_count} کالا)`}
                </Checkbox>
              </div>
            ))}
          </div>
        ) : (
          <Radio.Group
            className="w-full"
            value={selectedValue == null ? undefined : String(selectedValue)}
            onChange={(event) => setSelectedValue(event.target.value)}
          >
            <div className="flex flex-col px-4 py-2">
              {currentOptions.map((option) => (
                <Radio
                  key={String(option.value)}
                  value={String(option.value)}
                  className="w-full border-b border-gray-22 py-4"
                >
                  {option.label}
                </Radio>
              ))}
            </div>
          </Radio.Group>
        )}
        <div className="mt-2 flex flex-col gap-2 px-4 py-3">
          <Button
            fullWidth
            size="lg"
            onClick={() => {
              if (activeFilter === 'categories') {
                onChangeFilter(activeFilter, selectedCategories);
              } else {
                onChangeFilter(activeFilter as string, selectedValue as string | null);
              }
              closeModal();
            }}
          >
            تایید
          </Button>
          <Button variant="ghost" size="lg" fullWidth onClick={closeModal}>
            بستن
          </Button>
        </div>
      </BottomSheet>
    </>
  );
};

export default DealDetailFilters;
